export interface SubjectCoords {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

export interface Faculty {
    name: string;
    mastery: number;
    target: number;
    description?: string;
}

export interface Skill {
    name: string;
    mastery: number;
    target: number;
    description?: string;
    profession?: string;
}

export interface CharacterSheet {
    faculties: Faculty[];
    skills: Skill[];
    integrity: number;
    sanity: number;
}

const BACKGROUNDS_PATH = "/images/rooms";

function probeImage(url: string): Promise<boolean> {
    return new Promise(resolve => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = url;
    });
}

class RoomState {
    /** Full-screen area background (area → default) */
    backgroundImage = $state<string | null>(null);
    /** Small room thumbnail shown in the side panel (room name only, null if missing) */
    roomImage = $state<string | null>(null);

    subjectCoords = $state<SubjectCoords | null>(null);
    /** Current room name, set by room enter events. */
    title = $state<string | null>(null);
    /** World/server title, set by world.title event. */
    worldTitle = $state("Lovecraft Country");
    /** Current area name, set by room enter events. */
    areaName = $state<string | null>(null);
    characterSheet = $state<CharacterSheet | null>(null);
    flickering = $state(false);
    flickerDuration = $state(600);
    logoNameImage = $state<string | null>(null);
    logoIconImage = $state<string | null>(null);

    private bgRoomName: string | null = null;
    private bgProbeSeq = 0;

    paneOnLeft = $derived.by(() => {
        if (!this.subjectCoords) return true;
        const cx = (this.subjectCoords.x1 + this.subjectCoords.x2) / 2;
        return cx >= 0.5;
    });

    constructor() {
        this.resolveBackground();
    }

    applyRoomEvent(payload: unknown) {
        if (!payload || typeof payload !== "object") return;
        const p = payload as Record<string, unknown>;

        if (typeof p.title === "string") {
            this.title = p.title;
            this.bgRoomName = p.title;
        }
        if (typeof p.area_name === "string") {
            this.areaName = p.area_name;
        }
        if (p.subject_coords && typeof p.subject_coords === "object") {
            const sc = p.subject_coords as Record<string, unknown>;
            this.subjectCoords = {
                x1: Number(sc.x1 ?? 0),
                y1: Number(sc.y1 ?? 0),
                x2: Number(sc.x2 ?? 1),
                y2: Number(sc.y2 ?? 1),
            };
        }
        this.resolveBackground();
    }

    applyCharacterSheet(payload: unknown) {
        if (!payload || typeof payload !== "object") return;
        const p = payload as Record<string, unknown>;

        const raw = Array.isArray(p.abilities) ? p.abilities
            : (p.abilities && typeof p.abilities === "object") ? Object.values(p.abilities as object)
            : [];

        const faculties: Faculty[] = [];
        const skills: Skill[] = [];
        for (const item of raw) {
            if (!item || typeof item !== "object") continue;
            const a = item as Record<string, unknown>;
            if (typeof a.name !== "string") continue;
            const parsed = {
                name: a.name,
                mastery: Number(a.mastery ?? 0),
                target: Number(a.target ?? 0),
                description: typeof a.description === "string" ? a.description : undefined,
            };
            if (a.is_inherent) {
                faculties.push(parsed);
            } else {
                skills.push({ ...parsed, profession: typeof a.profession === "string" ? a.profession : undefined });
            }
        }

        this.characterSheet = {
            faculties,
            skills,
            integrity: typeof p.integrity === "number" ? p.integrity : 100,
            sanity: typeof p.sanity === "number" ? p.sanity : 100,
        };
    }

    applyBrandingEvent(payload: unknown) {
        if (!payload || typeof payload !== "object") return;
        const p = payload as Record<string, unknown>;
        if (typeof p.logo_name === "string") this.logoNameImage = p.logo_name;
        if (typeof p.logo_icon === "string") this.logoIconImage = p.logo_icon;
    }

    triggerFlicker(duration = 600) {
        this.flickerDuration = duration;
        this.flickering = false;
        requestAnimationFrame(() => { this.flickering = true; });
        setTimeout(() => { this.flickering = false; }, duration + 100);
    }

    // Called by RoomPanel's <img onerror> when the room thumbnail 404s or fails to decode.
    clearRoomImage() { this.roomImage = null; }

    reset() {
        this.title = null;
        this.areaName = null;
        this.worldTitle = "Lovecraft Country";
        this.roomImage = null;
        this.backgroundImage = null;
        this.bgRoomName = null;
        this.subjectCoords = null;
        this.characterSheet = null;
    }

    private resolveBackground() {
        const seq = ++this.bgProbeSeq;
        const roomName = this.bgRoomName;
        const areaName = this.areaName;

        const toFilename = (s: string) => s.replace(/[^A-Za-z]+/g, "_").replace(/^_|_$/g, "");

        // Room thumbnail: set immediately, let the <img> onerror handle failure.
        this.roomImage = roomName ? `${BACKGROUNDS_PATH}/${toFilename(roomName)}.png` : null;

        // Area background still needs a probe because CSS background-image has no onerror.
        const areaUrl = areaName ? `${BACKGROUNDS_PATH}/${toFilename(areaName + "_Background")}.png` : null;
        const dfltUrl = `${BACKGROUNDS_PATH}/Default_Background.png`;

        Promise.all([
            areaUrl ? probeImage(areaUrl) : Promise.resolve(false),
            probeImage(dfltUrl),
        ]).then(([areaOk, dfltOk]) => {
            if (seq !== this.bgProbeSeq) return;
            this.backgroundImage = areaOk && areaUrl ? areaUrl
                                 : dfltOk            ? dfltUrl
                                 : null;
        });
    }
}

export const room = new RoomState();
